import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { BuildingIcon, PlusIcon, SearchIcon, MapPinIcon, PhoneIcon, UserIcon, EditIcon, TrashIcon, NavigationIcon, CheckCircleIcon, XCircleIcon, MapIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Client } from '../types';
import L from 'leaflet';
import { LeafletMap } from '../components/ui/LeafletMap';
import { hasGoogleApiKey, searchGooglePlaces } from '../lib/googleMaps';
export function ClientsPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const {
    clients,
    users,
    addClient,
    updateClient,
    deleteClient,
    getCurrentLocation
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'location'>('info');
  const [isSaving, setIsSaving] = useState(false);
  // Address search state
  const [addressSearch, setAddressSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    display_name: string;
    lat: string;
    lon: string;
  }>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Form state
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    telefono: '',
    ciudad: '',
    limite: '',
    cedula: '',
    rnc: '',
    ruta: '',
    direccion: '',
    vendedorId: '',
    vendedorNombre: '',
    dependencia: '',
    zona: '',
    lat: null as number | null,
    lng: null as number | null
  });
  // Map refs
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const userLocationMarkerRef = useRef<L.Marker | null>(null);
  const mapDestroyedRef = useRef(false);
  const vendors = users.filter(u => u.rolNombre !== 'Administrador' && u.activo && u.permisos?.['new-visit'] === true);
  const vendorOptions = [{
    value: '',
    label: 'Sin vendedor asignado'
  }, ...vendors.map(v => ({
    value: v.id,
    label: v.nombre
  }))];
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  const filteredClients = clients.filter(c => {
    const query = searchQuery.toLowerCase();
    return c.nombre.toLowerCase().includes(query) || c.zona.toLowerCase().includes(query) || c.codigo && c.codigo.toLowerCase().includes(query);
  });
  const totalPages = Math.ceil(filteredClients.length / ITEMS_PER_PAGE);
  const paginatedClients = filteredClients.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const handleMapReady = (map: L.Map) => {
    mapInstanceRef.current = map;
    mapDestroyedRef.current = false;
    // Setup map interactions
    map.on('click', (e: L.LeafletMouseEvent) => {
      if (mapDestroyedRef.current) return;
      const {
        lat,
        lng
      } = e.latlng;
      setFormData(prev => ({
        ...prev,
        lat,
        lng
      }));
      updateMarker(lat, lng);
    });
    // Initial marker if data exists
    if (formData.lat && formData.lng) {
      updateMarker(formData.lat, formData.lng);
      map.setView([formData.lat, formData.lng], 15);
    }
    // Show user location if available
    getCurrentLocation().then(loc => {
      if (!loc || mapDestroyedRef.current) return;
      const userIcon = L.divIcon({
        html: `
          <div style="position:relative;width:20px;height:20px;">
            <div style="position:absolute;inset:0;background:#3b82f6;border-radius:50%;opacity:0.25;animation:ping 1.5s cubic-bezier(0,0,0.2,1) infinite;"></div>
            <div style="position:absolute;inset:3px;background:#3b82f6;border-radius:50%;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>
          </div>
        `,
        className: '',
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      });
      const userMarker = L.marker([loc.lat, loc.lng], {
        icon: userIcon
      }).addTo(map).bindPopup('<div style="font-family:system-ui;font-size:13px;font-weight:700;color:#1e40af;">📍 Tu ubicación actual</div>', {
        maxWidth: 180
      });
      userLocationMarkerRef.current = userMarker;
      // If no client location set yet, center on user
      if (!formData.lat && !formData.lng) {
        map.setView([loc.lat, loc.lng], 15);
      }
    });
  };
  const updateMarker = (lat: number, lng: number) => {
    if (!mapInstanceRef.current) return;
    // Custom red teardrop SVG icon — avoids broken default Leaflet icon
    const pinIcon = L.divIcon({
      html: `
        <div style="position:relative;width:30px;height:40px;">
          <svg viewBox="0 0 24 32" width="30" height="40" xmlns="http://www.w3.org/2000/svg"
            style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.35));display:block;">
            <path d="M12 0C7.58 0 4 3.58 4 8c0 5.25 8 16 8 16s8-10.75 8-16c0-4.42-3.58-8-8-8z"
              fill="#dc2626" stroke="white" stroke-width="1.5"/>
            <circle cx="12" cy="8" r="3.5" fill="white" opacity="0.95"/>
          </svg>
        </div>`,
      className: '',
      iconSize: [30, 40],
      iconAnchor: [15, 40]
    });
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      const marker = L.marker([lat, lng], {
        draggable: true,
        icon: pinIcon
      }).addTo(mapInstanceRef.current);
      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        setFormData(prev => ({
          ...prev,
          lat: pos.lat,
          lng: pos.lng
        }));
      });
      markerRef.current = marker;
    }
  };
  // Update marker when GPS/search sets location
  useEffect(() => {
    if (mapDestroyedRef.current) return;
    if (!mapInstanceRef.current || !formData.lat || !formData.lng) return;
    updateMarker(formData.lat, formData.lng);
    mapInstanceRef.current.setView([formData.lat, formData.lng], 15);
  }, [formData.lat, formData.lng]);
  const openAddModal = () => {
    setEditingClient(null);
    setFormData({
      codigo: '',
      nombre: '',
      telefono: '',
      ciudad: '',
      limite: '',
      cedula: '',
      rnc: '',
      ruta: '',
      direccion: '',
      vendedorId: '',
      vendedorNombre: '',
      dependencia: '',
      zona: '',
      lat: null,
      lng: null
    });
    setAddressSearch('');
    setSearchResults([]);
    setShowResults(false);
    setActiveTab('info');
    setIsModalOpen(true);
  };
  const openEditModal = (client: Client) => {
    setEditingClient(client);
    setFormData({
      codigo: client.codigo || '',
      nombre: client.nombre,
      telefono: client.telefono || '',
      ciudad: client.ciudad || '',
      limite: client.limite ? String(client.limite) : '',
      cedula: client.cedula || '',
      rnc: client.rnc || '',
      ruta: client.ruta || '',
      direccion: client.direccion,
      vendedorId: client.vendedorId || '',
      vendedorNombre: client.vendedorNombre || '',
      dependencia: client.dependencia || '',
      zona: client.zona,
      lat: client.lat || null,
      lng: client.lng || null
    });
    setAddressSearch('');
    setSearchResults([]);
    setShowResults(false);
    setActiveTab('info');
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    }
  };
  const handleUseCurrentLocation = async () => {
    setLocationError('');
    setIsGettingLocation(true);
    try {
      await new Promise<void>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('GPS no disponible en este dispositivo'));
          return;
        }
        navigator.geolocation.getCurrentPosition(pos => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          setFormData(prev => ({
            ...prev,
            lat,
            lng
          }));
          // Update user location marker on map
          if (mapInstanceRef.current && userLocationMarkerRef.current) {
            userLocationMarkerRef.current.setLatLng([lat, lng]);
            mapInstanceRef.current.setView([lat, lng], 17);
          } else if (mapInstanceRef.current) {
            mapInstanceRef.current.setView([lat, lng], 17);
          }
          resolve();
        }, err => {
          if (err.code === err.PERMISSION_DENIED) {
            reject(new Error('Permiso de ubicación denegado. Actívalo en la configuración del navegador.'));
          } else if (err.code === err.POSITION_UNAVAILABLE) {
            reject(new Error('Ubicación no disponible. Verifica que el GPS esté activado.'));
          } else {
            reject(new Error('No se pudo obtener la ubicación. Intenta nuevamente.'));
          }
        }, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });
    } catch (err: any) {
      setLocationError(err.message);
    } finally {
      setIsGettingLocation(false);
    }
  };
  const handleSave = async () => {
    if (!formData.nombre || !formData.zona || !formData.direccion) return;
    setIsSaving(true);
    try {
      const selectedVendor = vendors.find(v => v.id === formData.vendedorId);
      const clientData = {
        codigo: formData.codigo,
        nombre: formData.nombre,
        telefono: formData.telefono,
        ciudad: formData.ciudad,
        limite: Number(formData.limite) || 0,
        cedula: formData.cedula,
        rnc: formData.rnc,
        ruta: formData.ruta,
        direccion: formData.direccion,
        vendedorId: formData.vendedorId || undefined,
        vendedorNombre: selectedVendor?.nombre || undefined,
        dependencia: formData.dependencia,
        zona: formData.zona,
        lat: formData.lat || undefined,
        lng: formData.lng || undefined
      };
      // We cast to any here because the addClient/updateClient types might not be fully updated in the context definition yet
      // but the underlying implementation handles the new fields if the migration ran.
      if (editingClient) {
        await updateClient(editingClient.id, clientData as any);
        addActivityLog('Editó farmacia', `Farmacia: ${formData.nombre} | Código: ${formData.codigo}`);
      } else {
        await addClient(clientData as any);
        addActivityLog('Creó farmacia', `Farmacia: ${formData.nombre} | Código: ${formData.codigo}`);
      }
      closeModal();
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingClient) return;
    setIsSaving(true);
    try {
      await deleteClient(editingClient.id);
      addActivityLog('Eliminó farmacia', `Farmacia: ${editingClient.nombre}`);
      setIsDeleteModalOpen(false);
      closeModal();
    } finally {
      setIsSaving(false);
    }
  };
  const handleAddressSearch = (query: string) => {
    setAddressSearch(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        // Try Google Places first if API key is available
        if (hasGoogleApiKey()) {
          const googleResults = await searchGooglePlaces(query);
          if (googleResults.length > 0) {
            setSearchResults(googleResults);
            setShowResults(true);
            setIsSearching(false);
            return;
          }
        }
        // Fallback to free geocoders
        const searchQuery = encodeURIComponent(query);
        const [photonRes, nominatimRes, nominatimPOI] = await Promise.all([fetch(`https://photon.komoot.io/api/?q=${searchQuery}&limit=8&lang=es&lat=18.48&lon=-69.93`).then(r => r.json()).catch(() => ({
          features: []
        })), fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${searchQuery}&limit=6&addressdetails=1&accept-language=es`, {
          headers: {
            'Accept-Language': 'es'
          }
        }).then(r => r.json()).catch(() => []), query.toLowerCase().includes('farmacia') ? Promise.resolve([]) : fetch(`https://nominatim.openstreetmap.org/search?format=json&q=farmacia+${searchQuery}&limit=4&countrycodes=do&addressdetails=1&accept-language=es`, {
          headers: {
            'Accept-Language': 'es'
          }
        }).then(r => r.json()).catch(() => [])]);
        const seen = new Set<string>();
        const merged: Array<{
          display_name: string;
          lat: string;
          lon: string;
        }> = [];
        if (photonRes.features) {
          for (const f of photonRes.features) {
            const lat = String(f.geometry.coordinates[1]);
            const lon = String(f.geometry.coordinates[0]);
            const key = `${parseFloat(lat).toFixed(4)},${parseFloat(lon).toFixed(4)}`;
            if (!seen.has(key)) {
              seen.add(key);
              const props = f.properties || {};
              const parts = [props.name, props.street, props.city || props.district, props.state, props.country].filter(Boolean);
              merged.push({
                display_name: parts.join(', '),
                lat,
                lon
              });
            }
          }
        }
        for (const result of [...nominatimRes, ...nominatimPOI]) {
          const key = `${parseFloat(result.lat).toFixed(4)},${parseFloat(result.lon).toFixed(4)}`;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(result);
          }
        }
        setSearchResults(merged.slice(0, 12));
        setShowResults(true);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 350);
  };
  const formatDisplayName = (display_name: string) => {
    return display_name.replace(/, República Dominicana$/, '').replace(/, Dominican Republic$/, '');
  };
  const handleSelectAddress = (result: {
    display_name: string;
    lat: string;
    lon: string;
  }) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setFormData(prev => ({
      ...prev,
      lat,
      lng
    }));
    setAddressSearch(result.display_name);
    setShowResults(false);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 0
    }).format(amount);
  };
  return <div className="space-y-4 sm:space-y-6" data-id="element-929" data-mp="2n4n7ied0suhq">
      <PageHeader title="Farmacias" subtitle="Gestión de farmacias y ubicaciones" icon={<BuildingIcon className="w-6 h-6" data-id="element-931" data-mp="3jtur3gu2wtj0" />} data-id="element-930" data-mp="2n5y8y7iztq5o">
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto" data-id="element-932" data-mp="3fsj13a7am6aa">
          <div className="relative flex-1 sm:w-64" data-id="element-933" data-mp="3b2cbp84yihqz">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-934" data-mp="3b116r4zrz3ya" />
            <input type="text" placeholder="Buscar por nombre, código o zona..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] shadow-sm" data-id="element-935" data-mp="362q0134s19ka" />
          </div>
          {isAdmin && <Button onClick={openAddModal} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-937" data-mp="jxxwxxdapoep" />} data-id="element-936" data-mp="jw3aufqafq91">
              <span className="hidden sm:inline" data-id="element-938" data-mp="nyhrquaecfxo">Nueva Farmacia</span>
              <span className="sm:hidden" data-id="element-939" data-mp="swt173ws4ofd">Nueva</span>
            </Button>}
        </div>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-940" data-mp="xubdn4a09ls4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5" data-id="element-941" data-mp="12m2wesmx6n7p">
          <div className="flex items-center gap-2.5 sm:gap-3" data-id="element-942" data-mp="17lxkypj830pl">
            <div className="p-2 sm:p-2.5 bg-blue-50 rounded-xl" data-id="element-943" data-mp="17n8pirkhschr">
              <BuildingIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" data-id="element-944" data-mp="1boc7pkq0ikm5" />
            </div>
            <div data-id="element-945" data-mp="1gmvtnl4lvwei">
              <p className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-946" data-mp="1gmdlbq4o5rq8">
                {clients.length}
              </p>
              <p className="text-[11px] sm:text-xs text-gray-500 font-medium" data-id="element-947" data-mp="1wxzktbjl3t84">
                Total Farmacias
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5" data-id="element-948" data-mp="2bugu7x87qxs1">
          <div className="flex items-center gap-2.5 sm:gap-3" data-id="element-949" data-mp="26wors99xzt9l">
            <div className="p-2 sm:p-2.5 bg-emerald-50 rounded-xl" data-id="element-950" data-mp="1j5rrjtrieke6">
              <MapPinIcon className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" data-id="element-951" data-mp="1e7hvlgxynfri" />
            </div>
            <div data-id="element-952" data-mp="1e5nb23qlppny">
              <p className="text-xl sm:text-2xl font-bold text-emerald-600" data-id="element-953" data-mp="1sw4c3lzbqdkb">
                {clients.filter(c => c.lat && c.lng).length}
              </p>
              <p className="text-[11px] sm:text-xs text-gray-500 font-medium" data-id="element-954" data-mp="1o4cp1lfdmpyx">
                Con GPS
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5" data-id="element-955" data-mp="vfxh1iunx7x3">
          <div className="flex items-center gap-2.5 sm:gap-3" data-id="element-956" data-mp="37426uo10s51c">
            <div className="p-2 sm:p-2.5 bg-orange-50 rounded-xl" data-id="element-957" data-mp="3bv24xwlvkt5f">
              <XCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" data-id="element-958" data-mp="3bwd9hyn5a4xl" />
            </div>
            <div data-id="element-959" data-mp="2x7qs7rdyj33q">
              <p className="text-xl sm:text-2xl font-bold text-orange-600" data-id="element-960" data-mp="2x433d5jgj4vc">
                {clients.filter(c => !c.lat || !c.lng).length}
              </p>
              <p className="text-[11px] sm:text-xs text-gray-500 font-medium" data-id="element-961" data-mp="3pvuzuig39q92">
                Sin GPS
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5" data-id="element-962" data-mp="3guf1xa6gnglq">
          <div className="flex items-center gap-2.5 sm:gap-3" data-id="element-963" data-mp="3kvqjcsf6kviu">
            <div className="p-2 sm:p-2.5 bg-purple-50 rounded-xl" data-id="element-964" data-mp="3ktxcpd4kibau">
              <MapIcon className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" data-id="element-965" data-mp="25dx69caoti05" />
            </div>
            <div data-id="element-966" data-mp="29gtsdczfrw8u">
              <p className="text-xl sm:text-2xl font-bold text-purple-600" data-id="element-967" data-mp="29hcyzxjmyy7k">
                {new Set(clients.map(c => c.zona)).size}
              </p>
              <p className="text-[11px] sm:text-xs text-gray-500 font-medium" data-id="element-968" data-mp="1frgi93xkux7h">
                Zonas
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Clients list */}
      <div className="animate-stagger-2" data-id="element-969" data-mp="16qi803wjptgg">
        {filteredClients.length === 0 ? <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200/60 shadow-sm p-5 text-center animate-fade-in" data-id="element-970" data-mp="s1vv08ue5dse">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-2xl mb-4" data-id="element-971" data-mp="wsubp9jakdw6">
              <BuildingIcon className="w-7 h-7 sm:w-8 sm:h-8 text-gray-400" data-id="element-972" data-mp="j2qxn5ukfqht" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1" data-id="element-973" data-mp="o1289isoq513">
              {searchQuery ? 'No se encontraron farmacias' : 'No hay farmacias registradas'}
            </h3>
            <p className="text-gray-500 text-sm" data-id="element-974" data-mp="e478kwkodyl8">
              {searchQuery ? 'Intenta con otro término de búsqueda' : 'Agrega tu primera farmacia para comenzar'}
            </p>
          </div> : <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-975" data-mp="4f0ck5lo98fg">
            {/* ── MOBILE CARD VIEW (< sm) ── */}
            <div className="sm:hidden divide-y divide-gray-100" data-id="element-976" data-mp="3vjgxd0qcv591">
              {paginatedClients.map(client => <div key={client.id} className="p-3.5" data-id="element-977" data-mp="dcsrh8nhee2b">
                  <div className="flex items-start justify-between gap-2" data-id="element-978" data-mp="9cse7rc4c8wu">
                    <div className="min-w-0 flex-1" data-id="element-979" data-mp="n5i4i7yojzzr">
                      <div className="flex items-center gap-2" data-id="element-980" data-mp="n72v5mhviysc">
                        <div className="p-1.5 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-981" data-mp="i6x3i6bltca9">
                          <BuildingIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-982" data-mp="x2d0ok7hdoav" />
                        </div>
                        <p className="font-semibold text-gray-900 text-sm truncate" data-id="element-983" data-mp="s54dg57h6u2r">
                          {client.nombre}
                        </p>
                      </div>
                      {client.codigo && <p className="text-[11px] text-gray-400 font-mono mt-0.5 ml-8" data-id="element-984" data-mp="146m81bcejwwi">
                          {client.codigo}
                        </p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" data-id="element-985" data-mp="1dxgxspouxemp">
                      {client.lat && client.lng ? <span className="w-5 h-5 flex items-center justify-center" data-id="element-986" data-mp="1mwkejn0jxhy1">
                          <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-987" data-mp="1myotczzzo1d3" />
                        </span> : <span className="w-5 h-5 flex items-center justify-center" data-id="element-988" data-mp="1wv9uvbcsuuc6">
                          <XCircleIcon className="w-4 h-4 text-gray-300" data-id="element-989" data-mp="3tntkq3ju135y" />
                        </span>}
                      {isAdmin && <>
                          <button onClick={() => openEditModal(client)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-990" data-mp="3r3drkahzz9kr">
                            <EditIcon className="w-4 h-4" data-id="element-991" data-mp="3hdc2bfdwrl49" />
                          </button>
                          <button onClick={() => {
                    setEditingClient(client);
                    setIsDeleteModalOpen(true);
                  }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-992" data-mp="3cfsqem4onkfh">
                            <TrashIcon className="w-4 h-4" data-id="element-993" data-mp="3v3nvziq55rxi" />
                          </button>
                        </>}
                    </div>
                  </div>
                  <div className="mt-1.5 ml-8 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500" data-id="element-994" data-mp="2jo8z0exxn02e">
                    {client.zona && client.zona !== 'null' && <span className="inline-flex items-center gap-1 text-blue-600 font-medium" data-id="element-995" data-mp="h5c04mf0psrx">
                        <MapPinIcon className="w-3 h-3" data-id="element-996" data-mp="2gfq4jtei5oo" />
                        {client.zona}
                      </span>}
                    {client.telefono && <span className="inline-flex items-center gap-1" data-id="element-997" data-mp="v4uveemqd9mt">
                        <PhoneIcon className="w-3 h-3" data-id="element-998" data-mp="v2qrhwou0amj" />
                        {client.telefono}
                      </span>}
                    {client.vendedorNombre && <span className="inline-flex items-center gap-1" data-id="element-999" data-mp="l8i4foikl9vr">
                        <UserIcon className="w-3 h-3" data-id="element-1000" data-mp="q3xb9wi5u5io" />
                        {client.vendedorNombre}
                      </span>}
                    {client.limite > 0 && <span className="font-medium text-gray-700" data-id="element-1001" data-mp="3axalle7vcnsq">
                        {formatCurrency(client.limite)}
                      </span>}
                  </div>
                  {client.direccion && <p className="mt-1 ml-8 text-[11px] text-gray-400 truncate" data-id="element-1002" data-mp="21ysl7z686yy4">
                      {client.direccion}
                    </p>}
                </div>)}
            </div>

            {/* ── DESKTOP TABLE (sm+) ── */}
            <div className="hidden sm:block overflow-x-auto" data-id="element-1003" data-mp="1a7uv92v8tsgc">
              <table className="w-full" data-id="element-1004" data-mp="159b9b2gnggnz">
                <thead data-id="element-1005" data-mp="15acuw0vakljy">
                  <tr className="bg-gray-50/80 border-b border-gray-100" data-id="element-1006" data-mp="1izwrsq26yolc">
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1007" data-mp="1j17zv22o6ii9">
                      Código
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1008" data-mp="qhswfdj3728u">
                      Nombre
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1009" data-mp="2lm90m98mscs1">
                      Teléfono
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1010" data-mp="2vd3otj5ftmg4">
                      Ciudad
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1011" data-mp="22p7toivio2lg">
                      Límite
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1012" data-mp="2cl2ffmv1edsg">
                      Cédula
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1013" data-mp="3o22b8sglfar8">
                      RNC
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1014" data-mp="1hdig3lidhztw">
                      Ruta
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1015" data-mp="17mnqc75x4i3p">
                      Dirección
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1016" data-mp="xorjhrk0k9be">
                      Vendedor
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1017" data-mp="oo32qp38juut">
                      Dependencia
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1018" data-mp="2j2cgf7egxx8q">
                      Ruta Zona
                    </th>
                    <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1019" data-mp="1mxx3xetp8g1l">
                      GPS
                    </th>
                    {isAdmin && <th className="px-4 py-3.5 w-16" data-id="element-1020" data-mp="1d83xes8eyr47"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-1021" data-mp="1i6xaijjsgg3u">
                  {paginatedClients.map(client => <tr key={client.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-1022" data-mp="1475yupgqvbt0">
                      <td className="px-4 py-3.5 text-sm text-gray-600 font-mono whitespace-nowrap" data-id="element-1023" data-mp="1945ue6fyww5v">
                        {client.codigo || '—'}
                      </td>
                      <td className="px-4 py-3.5" data-id="element-1024" data-mp="z8ungaaswt8o">
                        <div className="flex items-center gap-3 min-w-[200px]" data-id="element-1025" data-mp="2omr5f41j6ji0">
                          <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-1026" data-mp="2tmdhvb9jbvpr">
                            <BuildingIcon className="w-4 h-4 text-blue-600" data-id="element-1027" data-mp="2tk9dytbmywph" />
                          </div>
                          <span className="font-semibold text-gray-900 text-sm truncate" title={client.nombre} data-id="element-1028" data-mp="hwnwcd54n9m2">
                            {client.nombre}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-1029" data-mp="10oyphf9zbt6z">
                        {client.telefono || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-1030" data-mp="wq93wzesf5c9">
                        {client.ciudad || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-900 font-medium whitespace-nowrap" data-id="element-1031" data-mp="15p4bofxhcnho">
                        {client.limite ? formatCurrency(client.limite) : '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-1032" data-mp="37hiudh7ulvz5">
                        {client.cedula || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-1033" data-mp="2ccb6wzb2hrsy">
                        {client.rnc || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-1034" data-mp="27ji44gz5stji">
                        {client.ruta || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 max-w-[200px] truncate" title={client.direccion} data-id="element-1035" data-mp="1xp89stqnpl65">
                        {client.direccion}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-1036" data-mp="1jofalvpeu1wp">
                        {client.vendedorNombre ? <div className="flex items-center gap-1.5 text-sm text-gray-700" data-id="element-1037" data-mp="3izyhm4r5l03q">
                            <UserIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-1038" data-mp="3e2yea1loubgb" />
                            <span className="font-medium" data-id="element-1039" data-mp="3llxw42ti3uxk">
                              {client.vendedorNombre}
                            </span>
                          </div> : <span className="text-xs text-gray-400 italic" data-id="element-1040" data-mp="3vazajokee49f">
                            Sin asignar
                          </span>}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-1041" data-mp="32nd8ikqtj7oo">
                        {client.dependencia || '—'}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-1042" data-mp="3ckrc3jrn81zk">
                        <Badge variant="info" size="sm" data-id="element-1043" data-mp="3cjg8bjy76u8g">
                          {client.zona}
                        </Badge>
                      </td>
                      <td className="px-4 py-3.5 text-center whitespace-nowrap" data-id="element-1044" data-mp="rv22pm9pl8e9">
                        {client.lat && client.lng ? <div className="inline-flex items-center gap-2" data-id="element-1045" data-mp="vuj8yhwym9kh">
                            <div className="inline-flex items-center gap-1.5" data-id="element-1046" data-mp="2svp2aq30hzv8">
                              <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-1047" data-mp="2ny6q8qtp74j0" />
                              <span className="text-xs text-emerald-600 font-medium" data-id="element-1048" data-mp="31x6evyh7njf6">
                                Sí
                              </span>
                            </div>
                            <a href={`https://www.google.com/maps?q=${client.lat},${client.lng}`} target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold text-blue-500 hover:text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded" title="Ver en Google Maps" data-id="element-1049" data-mp="298r2ljpgrf7j">
                              📍 Maps
                            </a>
                            <a href={`https://www.google.com/maps/dir/?api=1&destination=${client.lat},${client.lng}`} target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold text-green-600 hover:text-green-800 bg-green-50 px-1.5 py-0.5 rounded" title="Cómo llegar" data-id="element-1050" data-mp="1ljq16gf1t4br">
                              🧭 Ir
                            </a>
                          </div> : <div className="inline-flex items-center gap-1.5" data-id="element-1051" data-mp="or8en0tnlkvl">
                            <div className="w-2 h-2 rounded-full bg-gray-300" data-id="element-1052" data-mp="aqxm7tcp3y8b" />
                            <span className="text-xs text-gray-400" data-id="element-1053" data-mp="at1o65r1an5y">No</span>
                          </div>}
                      </td>
                      {isAdmin && <td className="px-4 py-3.5" data-id="element-1054" data-mp="2vj99e88463ka">
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(client)} data-id="element-1055" data-mp="30gj3402jd1dx">
                            <EditIcon className="w-4 h-4 text-gray-400" data-id="element-1056" data-mp="3aye8tc9ere9s" />
                          </Button>
                        </td>}
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredClients.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1057" data-mp="2il2trcpdl6z" />

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal} title={editingClient ? 'Editar Farmacia' : 'Nueva Farmacia'} size="lg" data-id="element-1058" data-mp="3kpinsbz32z4m">
        <ModalBody data-id="element-1059" data-mp="2e6ua04dz7iz5">
          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-6" data-id="element-1060" data-mp="29a3rzbim2db3">
            <button onClick={() => setActiveTab('info')} className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors ${activeTab === 'info' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1061" data-mp="29bx06v8vh5l7">
              Información
            </button>
            <button onClick={() => setActiveTab('location')} className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors ${activeTab === 'location' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1062" data-mp="2dutcwajmauha">
              Ubicación GPS
            </button>
          </div>

          {activeTab === 'info' && <div className="space-y-4" data-id="element-1063" data-mp="2wtnppdzitkut">
              <div className="grid grid-cols-2 gap-4" data-id="element-1064" data-mp="2wsmd352uwqaz">
                <Input label="Código" value={formData.codigo} onChange={e => setFormData({
              ...formData,
              codigo: e.target.value
            })} placeholder="Ej: CLI-001" data-id="element-1065" data-mp="2wrb7r0trjahr" />
                <Input label="Nombre de la Farmacia" value={formData.nombre} onChange={e => setFormData({
              ...formData,
              nombre: e.target.value
            })} placeholder="Ej: Farmacia Central" data-id="element-1066" data-mp="ph5hhxae6xyc" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-1067" data-mp="18dybjvrdw2tq">
                <Input label="RNC" value={formData.rnc} onChange={e => setFormData({
              ...formData,
              rnc: e.target.value
            })} placeholder="Ej: 101-12345-6" data-id="element-1068" data-mp="1n3mawrg08ret" />
                <Input label="Cédula" value={formData.cedula} onChange={e => setFormData({
              ...formData,
              cedula: e.target.value
            })} placeholder="Ej: 001-1234567-8" data-id="element-1069" data-mp="1s408tgnw6kz4" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-1070" data-mp="26oml168dlj4h">
                <Input label="Teléfono" value={formData.telefono} onChange={e => setFormData({
              ...formData,
              telefono: e.target.value
            })} placeholder="809-555-1234" data-id="element-1071" data-mp="26r8tr97064oa" />
                <Input label="Ciudad" value={formData.ciudad} onChange={e => setFormData({
              ...formData,
              ciudad: e.target.value
            })} placeholder="Ej: Santo Domingo" data-id="element-1072" data-mp="1n286dpfry2z1" />
              </div>

              <Input label="Dirección" value={formData.direccion} onChange={e => setFormData({
            ...formData,
            direccion: e.target.value
          })} placeholder="Ej: Calle El Conde #123" data-id="element-1073" data-mp="1ojf6mvbbjntm" />

              <div className="grid grid-cols-2 gap-4" data-id="element-1074" data-mp="1alyud36cczty">
                <Input label="Ruta" value={formData.ruta} onChange={e => setFormData({
              ...formData,
              ruta: e.target.value
            })} placeholder="Ej: Ruta 1" data-id="element-1075" data-mp="15s4eybxrr70o" />
                <Input label="Ruta Zona" value={formData.zona} onChange={e => setFormData({
              ...formData,
              zona: e.target.value
            })} placeholder="Ej: Zona Colonial" data-id="element-1076" data-mp="rv8vdpjw5x6d" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-1077" data-mp="38gm818mcdmi7">
                <Input label="Límite de Crédito (DOP)" type="number" value={formData.limite} onChange={e => setFormData({
              ...formData,
              limite: e.target.value
            })} placeholder="0.00" data-id="element-1078" data-mp="3chxyfgd1ibrg" />
                <Input label="Dependencia" value={formData.dependencia} onChange={e => setFormData({
              ...formData,
              dependencia: e.target.value
            })} placeholder="Ej: Casa Matriz" data-id="element-1079" data-mp="2tj3nkigp5vgk" />
              </div>

              {/* Vendedor asignado */}
              <div data-id="element-1080" data-mp="kmlioci0xaye">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1081" data-mp="kjpngvxk90dn">
                  Vendedor Asignado
                </label>
                <select value={formData.vendedorId} onChange={e => {
              const v = vendors.find(v => v.id === e.target.value);
              setFormData({
                ...formData,
                vendedorId: e.target.value,
                vendedorNombre: v?.nombre || ''
              });
            }} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1082" data-mp="uc56zxoi9x0n">
                  {vendorOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1083" data-mp="3f3x9wp6sx1vf">
                      {opt.label}
                    </option>)}
                </select>
              </div>
            </div>}

          {activeTab === 'location' && <div className="space-y-4" data-id="element-1084" data-mp="2xta3cuttgfge">
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-200" data-id="element-1085" data-mp="2xq5ql92acbe4">
                <p className="text-sm text-blue-700" data-id="element-1086" data-mp="2sv8j46knma3c">
                  <strong data-id="element-1087" data-mp="3qhfp9kwlwaht">Instrucciones:</strong> Busca una dirección, haz clic
                  en el mapa, o usa el botón de ubicación actual.
                  {hasGoogleApiKey() ? <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-lg text-xs font-semibold text-emerald-700 border border-emerald-200" data-id="element-1088" data-mp="3gigpysxqdr7h">
                      ✓ Google Maps Places activo
                    </span> : <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-lg text-xs font-semibold text-blue-700 border border-blue-200" data-id="element-1089" data-mp="251i274m8p5wc">
                      🔍 Búsqueda con Photon + OpenStreetMap
                    </span>}
                </p>
              </div>

              <div className="flex gap-2" data-id="element-1090" data-mp="1sl8whs6oritd">
                <div className="relative flex-1" data-id="element-1091" data-mp="1skflgf0p275f">
                  <div className="relative" data-id="element-1092" data-mp="1xkbpge9e5ykz">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1093" data-mp="1xj0o0l3f52x1" />
                    <input type="text" placeholder="Buscar en República Dominicana..." value={addressSearch} onChange={e => handleAddressSearch(e.target.value)} onFocus={() => searchResults.length > 0 && setShowResults(true)} onBlur={() => setTimeout(() => setShowResults(false), 200)} className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1094" data-mp="1jrcd2l4wokhn" />
                    {isSearching && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" data-id="element-1095" data-mp="v7sikadlap1q" />}
                  </div>
                  {showResults && searchResults.length > 0 && <div className="absolute z-[9999] top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden" data-id="element-1096" data-mp="1543v91h3dgwp">
                      {searchResults.map((result, i) => <button key={i} onMouseDown={() => handleSelectAddress(result)} className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors" data-id="element-1097" data-mp="19wx0dqgh2r9b" data-mpi={i}>
                          <div className="flex items-start gap-2" data-id="element-1098" data-mp="230j17kyoixxp">
                            <MapPinIcon className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1099" data-mp="271ba2d3n30tg" />
                            <span className="line-clamp-2" data-id="element-1100" data-mp="26yym9jb79w8h">
                              {formatDisplayName(result.display_name)}
                            </span>
                          </div>
                        </button>)}
                      <a href={`https://www.google.com/maps/search/${encodeURIComponent(addressSearch)}`} target="_blank" rel="noopener noreferrer" onMouseDown={e => e.preventDefault()} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-[#4285f4] hover:bg-blue-50 border-t border-gray-200 transition-colors" data-id="element-1101" data-mp="1k26oxqch17xw">
                        <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" data-id="element-1102" data-mp="39f0f89y20rq9">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-1103" data-mp="st3l1b9v8g0x" />
                          <circle cx="12" cy="9" r="2.5" fill="white" data-id="element-1104" data-mp="j2ioruhnznl6" />
                        </svg>
                        Buscar "{addressSearch}" en Google Maps
                        <svg className="w-3.5 h-3.5 ml-auto flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-1105" data-mp="a35bsruc0gvp">
                          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" data-id="element-1106" data-mp="1zhjuauerb7ir" />
                        </svg>
                      </a>
                    </div>}
                  {showResults && searchResults.length === 0 && !isSearching && addressSearch.length >= 2 && <div className="absolute z-[9999] top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden" data-id="element-1107" data-mp="3f0rjpypq2drs">
                        <div className="p-4 text-sm text-gray-500 text-center" data-id="element-1108" data-mp="3k0m7vui44p95">
                          No se encontraron resultados
                        </div>
                        <a href={`https://www.google.com/maps/search/${encodeURIComponent(addressSearch)}`} target="_blank" rel="noopener noreferrer" onMouseDown={e => e.preventDefault()} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-[#4285f4] hover:bg-blue-50 border-t border-gray-200 transition-colors" data-id="element-1109" data-mp="gjnkpbjdkkqg">
                          <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" data-id="element-1110" data-mp="2jr4qh28sbmj8">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-1111" data-mp="2tq3s40v4uhwq" />
                            <circle cx="12" cy="9" r="2.5" fill="white" data-id="element-1112" data-mp="211y517glwgtq" />
                          </svg>
                          Buscar "{addressSearch}" en Google Maps
                          <svg className="w-3.5 h-3.5 ml-auto flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-1113" data-mp="2arhpghjywiqp">
                            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" data-id="element-1114" data-mp="1dcdheb9icve4" />
                          </svg>
                        </a>
                      </div>}
                </div>
                <Button variant="secondary" onClick={handleUseCurrentLocation} isLoading={isGettingLocation} leftIcon={<NavigationIcon className="w-4 h-4" data-id="element-1116" data-mp="15stluy3vesb9" />} data-id="element-1115" data-mp="wrlw54ff6zss">
                  Mi ubicación
                </Button>
              </div>

              {locationError && <div className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-1117" data-mp="1fp6cto816s2b">
                  <span data-id="element-1118" data-mp="1khq23ig2zbmy">⚠️</span>
                  <span data-id="element-1119" data-mp="1pg1adoqq9e32">{locationError}</span>
                </div>}

              <div className="grid grid-cols-2 gap-3" data-id="element-1120" data-mp="1pi42qffcrjay">
                <div data-id="element-1121" data-mp="1tinvl6sw80ra">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1122" data-mp="1tjft4n61w8k7">
                    Latitud
                  </label>
                  <div className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 font-mono" data-id="element-1123" data-mp="3iyi4avgjdq08">
                    {formData.lat ? formData.lat.toFixed(6) : <span className="text-gray-400" data-id="element-1124" data-mp="39yllhagtvld3">—</span>}
                  </div>
                </div>
                <div data-id="element-1125" data-mp="351bo98n765ep">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1126" data-mp="5jlrplo7dinc">
                    Longitud
                  </label>
                  <div className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 font-mono" data-id="element-1127" data-mp="1jjnp5uzwd4w">
                    {formData.lng ? formData.lng.toFixed(6) : <span className="text-gray-400" data-id="element-1128" data-mp="p7uknxxklsnw">—</span>}
                  </div>
                </div>
              </div>

              <div className="h-[300px] w-full rounded-xl border border-gray-200 overflow-hidden relative z-0" data-id="element-1129" data-mp="kgck3azglu3g">
                <LeafletMap onMapReady={handleMapReady} center={formData.lat && formData.lng ? [formData.lat, formData.lng] : undefined} data-id="element-1130" data-mp="cbep24rskm9e" />
              </div>

              {formData.lat && formData.lng && <div className="space-y-3" data-id="element-1131" data-mp="v9iq0bxizf04">
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-200" data-id="element-1132" data-mp="v87l28scg17f">
                    <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-1133" data-mp="r7x7a9yb86i0" />
                    <div className="flex-1" data-id="element-1134" data-mp="14yshbpkjotn6">
                      <p className="text-sm font-medium text-emerald-800" data-id="element-1135" data-mp="100r0zcei78fe">
                        Ubicación seleccionada
                      </p>
                      <p className="text-xs text-emerald-600 font-mono" data-id="element-1136" data-mp="1ey9j3vwoendv">
                        {formData.lat.toFixed(6)}, {formData.lng.toFixed(6)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2" data-id="element-1137" data-mp="1tim3dsck14aw">
                    <a href={`https://www.google.com/maps?q=${formData.lat},${formData.lng}`} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#4285f4] hover:bg-[#3367d6] text-white rounded-xl text-sm font-semibold transition-colors" data-id="element-1138" data-mp="1xl0mmdk4y62p">
                      📍 Ver en Google Maps
                    </a>
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${formData.lat},${formData.lng}`} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#34a853] hover:bg-[#2d9249] text-white rounded-xl text-sm font-semibold transition-colors" data-id="element-1139" data-mp="10w19ol8lwg46">
                      🧭 Cómo llegar
                    </a>
                  </div>
                </div>}
            </div>}
        </ModalBody>
        <ModalFooter data-id="element-1140" data-mp="320b9a2vy3rm">
          <div className="flex items-center justify-between w-full" data-id="element-1141" data-mp="33bijjvjhtnh">
            {editingClient && isAdmin ? <Button variant="danger" onClick={() => setIsDeleteModalOpen(true)} leftIcon={<TrashIcon className="w-4 h-4" data-id="element-1143" data-mp="vouvc5vsj14k" />} data-id="element-1142" data-mp="3u9w10f7j3t8w">
                Eliminar
              </Button> : <div data-id="element-1144" data-mp="lshtulu07iz9" />}
            <div className="flex items-center gap-3" data-id="element-1145" data-mp="lrgg29llsidu">
              <Button variant="secondary" onClick={closeModal} data-id="element-1146" data-mp="gtoojgoveya6">
                Cancelar
              </Button>
              <Button onClick={handleSave} isLoading={isSaving} disabled={!formData.nombre || !formData.zona || !formData.direccion} data-id="element-1147" data-mp="36iqly29ex4zn">
                {editingClient ? 'Guardar Cambios' : 'Crear Farmacia'}
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Farmacia" size="sm" data-id="element-1148" data-mp="1r0avmir96eln">
        <ModalBody data-id="element-1149" data-mp="1vs2ee7463g18">
          <p className="text-gray-600" data-id="element-1150" data-mp="1vskfpi5oskg0">
            ¿Estás seguro de que deseas eliminar{' '}
            <strong data-id="element-1151" data-mp="20s6uhw15y8qx">{editingClient?.nombre}</strong>? Esta acción no se puede
            deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-1152" data-mp="29q0srcihncgq">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-1153" data-mp="29rtz0qp6vuo7">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} isLoading={isSaving} data-id="element-1154" data-mp="fchozkxv6xmz">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}